import React from "react";
import PropTypes from "kf-proptypes/widgets";

import { EMAIL_TYPE, EMAIL_SIZE } from "./constants.js";
import { FieldErrors, FieldHint, FieldLabel } from "../index.jsx";
import { EditableEmail } from "./editable.jsx";
import { ReadonlyEmail } from "./readonly.jsx";

export { EMAIL_SIZE, EMAIL_TYPE as EmailType };
export function Email({
  value,
  label,
  hint,
  errors,
  required,
  id,
  onBlur,
  onFocus,
  onChange,
  readOnly,
  disabled,
  onKeyDown,
  autoFocus,
  className,
  isError,
  name,
  styleType,
  autoComplete,
  handleReadonlyLink,
  readonlyWrap,
  onEnter,
  wrapperClassName,
  size,
  showError
}) {
  return (
    <>
      {label && (
        <FieldLabel
          disabled={disabled}
          label={label}
          targetId={id}
          required={required}
          size={size}
        />
      )}
      {readOnly ? (
        <ReadonlyEmail
          name={name}
          id={id}
          className={className}
          wrapperClassName={wrapperClassName}
          value={value}
          styleType={styleType}
          errors={errors}
          isError={isError}
          handleReadonlyLink={handleReadonlyLink}
          readonlyWrap={readonlyWrap}
          size={size}
        />
      ) : (
        <EditableEmail
          name={name}
          id={id}
          className={className}
          wrapperClassName={wrapperClassName}
          autoFocus={autoFocus}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          disabled={disabled}
          value={value}
          onKeyDown={onKeyDown}
          styleType={styleType}
          autoComplete={autoComplete}
          errors={errors}
          isError={isError}
          handleReadonlyLink={handleReadonlyLink}
          readonlyWrap={readonlyWrap}
          onEnter={onEnter}
          size={size}
        />
      )}
      {hint && <FieldHint hint={hint} targetId={id} />}
      {showError && errors && errors.length > 0 && (
        <FieldErrors errors={errors} targetId={id} />
      )}
    </>
  );
}

Email.propTypes = {
  /** Size of the form control. (BASE | SMALL) */
  size: PropTypes.oneOf(Object.values(EMAIL_SIZE)),
  /**
   * email value which user enter in input field
   */
  value: PropTypes.string.isRequired,
  /**
   * onBlur for email field
   */
  onBlur: PropTypes.func,
  /**
   * onFocus for email field
   */
  onFocus: PropTypes.func,
  /**
   * onChange will trigger when user enters any value in input field
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Indicates whether email field is readOnly or not
   */
  readOnly: PropTypes.bool,
  /**
   * Indicates whether email field is disabled or not
   */
  disabled: PropTypes.bool,
  /**
   * onKeyDown will enables when user enter any value
   */
  onKeyDown: PropTypes.func,
  /**
   * autoFocus for email field
   */
  autoFocus: PropTypes.bool,
  autoComplete: PropTypes.string,
  /**
   * Indicates whether email field is having any error or not.
   */
  isError: PropTypes.bool,
  /**
   * name is for email input field
   */
  name: PropTypes.string.isRequired,
  /**
   * className will change ui for email field
   */
  className: PropTypes.string,
  /**
   * styleType of the email field
   */
  styleType: PropTypes.oneOf(Object.values(EMAIL_TYPE)),
  /**
   * label for Email component.
   */
  label: PropTypes.string,
  /**
   * hint for Email component.
   */
  hint: PropTypes.string,
  /**
   * consumer can pass multiple error state to Email component to show user.
   */
  errors: PropTypes.arrayOf(PropTypes.string),
  /**
   * email field is required or not
   */
  required: PropTypes.bool,
  /**
   * unique Id for email
   */
  id: PropTypes.string,
  /**
   * handleReadonlyLink
   */
  showError: PropTypes.bool,
  handleReadonlyLink: PropTypes.bool,
  readonlyWrap: PropTypes.bool,
  onEnter: PropTypes.func,
  wrapperClassName: PropTypes.string
};

Email.defaultProps = {
  size: EMAIL_SIZE.BASE,
  handleBlur: () => {},
  handleFocus: () => {},
  handleChange: () => {},
  onEnter: () => {},
  styleType: EMAIL_TYPE.NORMAL,
  value: "",
  wrapperClassName: "",
  className: "",
  name: "",
  isError: false,
  disabled: false,
  handleReadonlyLink: true,
  readonlyWrap: false,
  showError: false
};
